import * as React from 'react';
import { useContext, useState } from 'react';

import type { SGASubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';

import _t from 'i18n!nls/grading';

export type SearchContextProviderType = {
  searchKeyword: string;
  setSearchKeyword: (val: string) => void;

  filters: FilterValues;
  setFilters: (val: FilterValues) => void;
};

export const filterGradingStatusValues = {
  all: _t('All'),
  graded: _t('Graded'),
  ungraded: _t('Ungraded'),
};

export type FilterGradingStatus = keyof typeof filterGradingStatusValues;

export type FilterValues = {
  gradingStatus: FilterGradingStatus;
};
const filterValues: FilterValues = {
  gradingStatus: 'all',
};

const SearchContext = React.createContext<SearchContextProviderType | undefined>(undefined);

export function useSearchContext() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('`useSearchContext` was used outside of `<SearchContextProvider>`.');
  }
  return context;
}

export const submitterFilterMatch = (submitter: SGASubmitter, searchKeyword: string, filters: FilterValues) => {
  const filterMatch =
    (filters.gradingStatus === 'graded' && submitter.isSubmissionGraded) ||
    (filters.gradingStatus === 'ungraded' && !submitter.isSubmissionGraded) ||
    filters.gradingStatus === 'all';

  const searchMatch = searchKeyword ? submitter.name.toLocaleLowerCase().indexOf(searchKeyword) > -1 : true;
  return filterMatch && searchMatch;
};

const SearchContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filters, setFilters] = useState<FilterValues>(filterValues);

  return (
    <SearchContext.Provider
      value={{
        searchKeyword,
        setSearchKeyword,
        filters,
        setFilters,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export default SearchContextProvider;
